<template>
  <v-text-field
    v-bind="$attrs"
    ref="textfield"
    dense
    placeholder=""
    :autocomplete="enableAddressAutoSuggest ? 'off' : 'on'"
    :model-value="localValue"
    @update:model-value="onInput"
    @change="onChange"
  />
</template>

<script>
import { FlewidTextFieldHelper } from '@/components/helpers/FlewidTextFieldHelper'
import { ExternalScript } from '@/utils/ExternalScript'
import { settings } from '@/utils/settings'

export default {
  inheritAttrs: false,

  props: {
    modelValue: {
      type: String,
      default: undefined
    },
    value: {
      type: String,
      default: undefined
    },
    enableAddressAutoSuggest: Boolean,
    addressAutoSuggestCountry: {
      type: String,
      default: 'au'
    }
  },

  emits: ['update:modelValue', 'input', 'change', 'placechanged'],

  data: () => ({
    autocomplete: null,
    autocompleteInput: null,
    autocompleteListener: null,
    initAutoCompleteTimeout: null,
    localValue: undefined
  }),

  watch: {
    modelValue() {
      this.syncLocalValue()
    },

    value() {
      this.syncLocalValue()
    },

    enableAddressAutoSuggest: {
      immediate: true,
      handler(enabled) {
        if (!enabled) {
          this.destroyAutocomplete()
          return
        }

        this.queueAutoCompleteInit()
      }
    }
  },

  methods: {
    externalValue() {
      return this.modelValue !== undefined ? this.modelValue : this.value
    },

    syncLocalValue() {
      const externalValue = this.externalValue()

      if (externalValue !== this.localValue) {
        this.localValue = externalValue
      }
    },

    onInput(value) {
      const resolvedValue = value?.target ? value.target.value : value
      const externalValue = this.externalValue()

      // Vuetify can emit an initial empty model update while hydrating the
      // text field. Ignore that one when we already have a saved external
      // value so DB-backed addresses stay visible on first render.
      if (
        resolvedValue === '' &&
        externalValue &&
        this.localValue === externalValue
      ) {
        return
      }

      if (resolvedValue !== this.localValue) {
        this.localValue = resolvedValue
      }

      this.$emit('update:modelValue', resolvedValue)
      this.$emit('input', resolvedValue)
    },

    onChange(value) {
      const resolvedValue = value?.target ? value.target.value : value
      this.$emit('change', resolvedValue)
    },

    onPlaceChanged() {
      const place = this.autocomplete?.getPlace()
      const formattedResult =
        FlewidTextFieldHelper.formatGooglePlaceResult(place)

      if (!formattedResult) {
        return
      }

      this.localValue = formattedResult.line1
      this.$emit('update:modelValue', formattedResult.line1)
      this.$emit('input', formattedResult.line1)
      this.$emit('placechanged', formattedResult, place)
    },

    resolveInput() {
      return this.$refs.textfield?.$el?.querySelector('input') ?? null
    },

    queueAutoCompleteInit() {
      if (this.initAutoCompleteTimeout) {
        return
      }

      this.initAutoCompleteTimeout = setTimeout(() => {
        this.initAutoCompleteTimeout = null
        this.initAutoComplete()
      }, 200)
    },

    destroyAutocomplete() {
      if (this.initAutoCompleteTimeout) {
        clearTimeout(this.initAutoCompleteTimeout)
        this.initAutoCompleteTimeout = null
      }

      if (this.autocompleteListener?.remove) {
        this.autocompleteListener.remove()
      }

      if (
        window.google?.maps?.event?.clearInstanceListeners &&
        this.autocomplete
      ) {
        window.google.maps.event.clearInstanceListeners(this.autocomplete)
      }

      this.autocomplete = null
      this.autocompleteInput = null
      this.autocompleteListener = null
    },

    initAutoComplete() {
      if (!this.enableAddressAutoSuggest) {
        return
      }

      if (!window.google?.maps?.places?.Autocomplete) {
        this.queueAutoCompleteInit()
        return
      }

      const input = this.resolveInput()
      if (!input) {
        this.$nextTick(() => this.queueAutoCompleteInit())
        return
      }

      if (this.autocomplete && this.autocompleteInput === input) {
        return
      }

      this.destroyAutocomplete()
      this.autocompleteInput = input
      this.autocomplete = new window.google.maps.places.Autocomplete(input, {
        componentRestrictions: {
          country: this.addressAutoSuggestCountry.toLowerCase()
        },
        fields: ['address_components', 'geometry'],
        types: ['address']
      })
      input.placeholder = ''

      this.autocompleteListener = this.autocomplete.addListener(
        'place_changed',
        this.onPlaceChanged
      )
    }
  },

  mounted() {
    this.syncLocalValue()
    ExternalScript.importScript(
      `https://maps.googleapis.com/maps/api/js?key=${settings.GOOGLE_PLACE_API_KEY}&libraries=places`
    )
    this.initAutoComplete()
  },

  updated() {
    this.initAutoComplete()
  },

  beforeUnmount() {
    this.destroyAutocomplete()
  }
}
</script>
